import { useEffect, useState } from "react";
import { addMonths, eachDayOfInterval, endOfMonth, format, getDay, isToday, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Truck, X } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const tone = (left, total) => left === 0 ? "bg-[#C86D51] text-white" : left <= 2 ? "bg-[#F2C46D] text-[#1C2421]" : "bg-[#DCE8DF] text-[#1E3A2B]";
const EMPTY = { confirmed: 0, pending: 0, left: 0, has_slots: false };

function SessionBar({ label, cell, total, testid }) {
  const c = cell || EMPTY;
  if (!c.has_slots) return (
    <div className={`flex items-center justify-between rounded px-1.5 py-0.5 text-[11px] ${c.confirmed + c.pending ? "bg-[#C86D51]/20 text-[#C86D51]" : "bg-[#F0ECE6] text-[#9AA39D]"}`} data-testid={testid}
      title={`${label}: no open slots${c.confirmed + c.pending ? ` (but ${c.confirmed + c.pending} jeep${c.confirmed + c.pending > 1 ? "s" : ""} booked)` : ""}`}>
      <span>{label}</span><span>{c.confirmed + c.pending ? `${c.confirmed + c.pending} booked` : "no slots"}</span>
    </div>
  );
  return (
    <div className={`flex items-center justify-between rounded px-1.5 py-0.5 text-[11px] ${tone(c.left, total)}`} data-testid={testid}
      title={`${label}: ${c.confirmed} paid, ${c.pending} pending, ${c.left} left of ${total}`}>
      <span>{label}</span>
      <span className="font-mono">{c.left}/{total}{c.pending > 0 && <span className="opacity-70"> ·{c.pending}p</span>}</span>
    </div>
  );
}

function DayPanel({ date, onClose, onDispatch }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/jeep-board/day", { params: { date } }).then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e))); }, [date]);
  return (
    <aside className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 p-6 overflow-y-auto" data-testid="jb-day-panel">
      <div className="flex items-start justify-between mb-5">
        <div><div className="jt-eyebrow">Jeep board</div><h3 className="font-serif text-2xl text-[#1E3A2B]">{format(new Date(`${date}T00:00:00`), "EEE dd MMM yyyy")}</h3></div>
        <button onClick={onClose} data-testid="jb-day-close"><X size={20}/></button>
      </div>
      <button onClick={() => onDispatch(date)} className="jt-pill-primary text-sm mb-6" data-testid="jb-open-dispatch"><Truck size={14}/> Open dispatch for this day</button>
      {!data ? <p className="text-sm text-[#5C6B63]">Loading…</p> : ["morning", "evening"].map((ses) => {
        const rows = data.bookings.filter((b) => b.pool_key.endsWith(ses));
        const used = rows.reduce((n, b) => n + b.vehicles, 0);
        return (
          <section key={ses} className="mb-6" data-testid={`jb-day-${ses}`}>
            <h4 className="text-sm font-semibold mb-2"><span className="capitalize">{ses}</span> · {data.open[ses] ? `${Math.max(data.total - used, 0)} of ${data.total} jeeps left` : "no open slots"}</h4>
            {rows.length === 0 ? <p className="text-xs text-[#5C6B63]">No bookings.</p> : rows.map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-[#EFE9E1] py-2 text-sm" data-testid={`jb-bk-${b.booking_ref}`}>
                <div><div className="font-medium">{b.guest_name}</div><div className="text-xs text-[#5C6B63]">{b.safari_name || b.title} · {b.slot_time} · {b.adults + (b.children || 0)} guests · {b.booking_ref}</div></div>
                <div className="text-right"><div className="font-mono">{b.vehicles} jeep{b.vehicles > 1 ? "s" : ""}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.status === "confirmed" ? "bg-[#DCE8DF] text-[#1E3A2B]" : "bg-[#F2C46D]"}`}>{b.status === "confirmed" ? "paid" : "pending"}</span></div>
              </div>
            ))}
          </section>
        );
      })}
      {data?.requests.length > 0 && (
        <section data-testid="jb-day-requests"><h4 className="text-sm font-semibold mb-2">On request</h4>
          {data.requests.map((r) => <div key={r.request_ref} className="text-sm py-1">{r.guest_name} · {r.safari_name} <span className="text-xs text-[#5C6B63]">({r.status})</span></div>)}
        </section>
      )}
    </aside>
  );
}

export default function JeepBoard({ onDispatch }) {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/jeep-board", { params: { month: format(month, "yyyy-MM") } }).then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e))); }, [month]);
  const days = eachDayOfInterval({ start: month, end: endOfMonth(month) });
  const byDate = Object.fromEntries((data?.days || []).map((d) => [d.date, d]));
  const lead = (getDay(month) + 6) % 7;

  return (
    <div className="mt-6" data-testid="jeep-board">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={() => setMonth(addMonths(month, -1))} className="p-2 rounded-full border border-[#E2DACF]" data-testid="jb-prev"><ChevronLeft size={16}/></button>
        <h3 className="font-serif text-2xl text-[#1E3A2B] w-48 text-center" data-testid="jb-month">{format(month, "MMMM yyyy")}</h3>
        <button onClick={() => setMonth(addMonths(month, 1))} className="p-2 rounded-full border border-[#E2DACF]" data-testid="jb-next"><ChevronRight size={16}/></button>
        <span className="text-sm text-[#5C6B63]" data-testid="jb-total">{data ? `${data.total} active jeeps per session` : ""}</span>
        <div className="flex gap-2 text-[11px] ml-auto">
          <span className="px-2 py-0.5 rounded bg-[#DCE8DF]">plenty</span><span className="px-2 py-0.5 rounded bg-[#F2C46D]">2 or fewer</span>
          <span className="px-2 py-0.5 rounded bg-[#C86D51] text-white">full</span><span className="px-2 py-0.5 rounded bg-[#F0ECE6] text-[#9AA39D]">no slots</span><span className="text-[#5C6B63]">·Np = pending holds</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-xs">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="jt-eyebrow text-center py-1">{d}</div>)}
        {Array.from({ length: lead }).map((_, i) => <div key={`x${i}`}/>)}
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const row = byDate[key];
          return (
            <button key={key} onClick={() => setOpen(key)} data-testid={`jb-day-${key}`}
              className={`text-left rounded-lg bg-white ring-1 p-1.5 space-y-1 min-h-[84px] transition-shadow hover:shadow-md ${isToday(d) ? "ring-[#1E3A2B]" : "ring-black/5"}`}>
              <div className="flex items-center justify-between"><span className="font-semibold">{format(d, "d")}</span>
                {row?.requests > 0 && <span className="text-[10px] px-1.5 rounded-full bg-[#1E3A2B] text-white" data-testid={`jb-req-${key}`}>{row.requests} req</span>}</div>
              {data && <SessionBar label="AM" cell={row?.morning} total={data.total} testid={`jb-am-${key}`}/>}
              {data && <SessionBar label="PM" cell={row?.evening} total={data.total} testid={`jb-pm-${key}`}/>}
            </button>
          );
        })}
      </div>
      {open && <><div className="fixed inset-0 bg-black/30 z-40" onClick={() => setOpen(null)}/><DayPanel date={open} onClose={() => setOpen(null)} onDispatch={(d) => { setOpen(null); onDispatch(d); }}/></>}
    </div>
  );
}
